import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Pause, Play } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Shared gold-circle Play/Pause button. Two size variants cover all
 * three player surfaces:
 *   - `mini` (36 px): MiniPlayer in the app shell
 *   - `big`  (64 px): Inline-Player on the Story page + Now-Playing full
 *     screen
 *
 * Icon swaps based on `isPlaying`; aria-labels are caller-provided so
 * each surface can route its own STRINGS entries without the primitive
 * owning localization.
 */
const audioPlayButtonVariants = cva(
  'flex shrink-0 items-center justify-center rounded-full bg-dream-gold text-dream-navy transition-colors hover:bg-dream-gold/90',
  {
    variants: {
      size: {
        mini: 'h-9 w-9',
        big: 'h-16 w-16',
      },
    },
    defaultVariants: { size: 'mini' },
  },
);

const audioPlayIconVariants = cva('', {
  variants: {
    size: {
      mini: 'h-5 w-5',
      big: 'h-8 w-8',
    },
  },
  defaultVariants: { size: 'mini' },
});

export interface AudioPlayButtonProps
  extends
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'onClick'>,
    VariantProps<typeof audioPlayButtonVariants> {
  isPlaying: boolean;
  onToggle: () => void;
  ariaLabelPlay: string;
  ariaLabelPause: string;
}

export const AudioPlayButton = forwardRef<HTMLButtonElement, AudioPlayButtonProps>(
  ({ isPlaying, onToggle, ariaLabelPlay, ariaLabelPause, size, className, ...props }, ref) => {
    const Icon = isPlaying ? Pause : Play;
    return (
      <button
        ref={ref}
        type="button"
        onClick={onToggle}
        aria-label={isPlaying ? ariaLabelPause : ariaLabelPlay}
        className={cn(audioPlayButtonVariants({ size }), className)}
        {...props}
      >
        <Icon className={audioPlayIconVariants({ size })} fill="currentColor" />
      </button>
    );
  },
);
AudioPlayButton.displayName = 'AudioPlayButton';
